import { useState, useEffect, useCallback } from 'react';

interface TouchCapabilities {
  isTouchDevice: boolean;
  hasCoarsePointer: boolean;
  hasFinePointer: boolean;
  maxTouchPoints: number;
  supportsHover: boolean;
  prefersTouchInteraction: boolean;
  isLargeScreen: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

interface ViewportInfo {
  width: number;
  height: number;
  orientation: 'portrait' | 'landscape';
  densityRatio: number;
}

export const useTouchDeviceDetection = () => {
  const [capabilities, setCapabilities] = useState<TouchCapabilities>({
    isTouchDevice: false,
    hasCoarsePointer: false,
    hasFinePointer: true,
    maxTouchPoints: 0,
    supportsHover: true,
    prefersTouchInteraction: false,
    isLargeScreen: false,
    isMobile: false,
    isTablet: false,
    isDesktop: true
  });

  const [viewport, setViewport] = useState<ViewportInfo>({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
    orientation: 'landscape',
    densityRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1
  });

  const detectCapabilities = useCallback((): TouchCapabilities => {
    if (typeof window === 'undefined') {
      return capabilities;
    }

    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const maxTouchPoints = navigator.maxTouchPoints || 0;
    
    // Media query checks
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    const supportsHover = window.matchMedia('(hover: hover)').matches;
    
    // Screen size detection
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isLargeScreen = width >= 1024;
    const isMobile = width <= 640;
    const isTablet = width > 640 && width < 1024;
    const isDesktop = width >= 1024;
    
    // Prefer touch interaction if it's a touch device without hover capability
    const prefersTouchInteraction = hasTouch && !supportsHover;

    return {
      isTouchDevice: hasTouch,
      hasCoarsePointer,
      hasFinePointer,
      maxTouchPoints,
      supportsHover,
      prefersTouchInteraction,
      isLargeScreen,
      isMobile,
      isTablet,
      isDesktop
    };
  }, []);

  const updateViewport = useCallback(() => {
    if (typeof window === 'undefined') return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const orientation = width > height ? 'landscape' : 'portrait';
    const densityRatio = window.devicePixelRatio || 1;

    setViewport({
      width,
      height,
      orientation,
      densityRatio
    });
  }, []);

  useEffect(() => {
    setCapabilities(detectCapabilities());
    updateViewport();

    // Listen for changes
    const handleResize = () => {
      setCapabilities(detectCapabilities());
      updateViewport();
    };

    const handleOrientationChange = () => {
      // Delay to ensure dimensions are updated
      setTimeout(() => {
        setCapabilities(detectCapabilities());
        updateViewport();
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [detectCapabilities, updateViewport]);

  // Helper functions for adaptive behavior
  const getOptimalTouchTarget = useCallback(() => {
    if (capabilities.prefersTouchInteraction) {
      return {
        minHeight: '44px',
        minWidth: '44px',
        padding: '12px'
      };
    }
    return {
      minHeight: '36px',
      minWidth: '36px',
      padding: '8px'
    };
  }, [capabilities.prefersTouchInteraction]);

  const getOptimalSpacing = useCallback(() => {
    if (capabilities.isMobile) {
      return {
        gap: '8px',
        padding: '12px',
        margin: '8px'
      };
    }
    if (capabilities.isTablet) {
      return {
        gap: '12px',
        padding: '16px',
        margin: '12px'
      };
    }
    return {
      gap: '16px',
      padding: '24px',
      margin: '16px'
    };
  }, [capabilities.isMobile, capabilities.isTablet]);

  const shouldUseNativeScrolling = useCallback(() => {
    return capabilities.isTouchDevice && (capabilities.isMobile || capabilities.isTablet);
  }, [capabilities]);

  const getDropdownBehavior = useCallback(() => {
    if (capabilities.prefersTouchInteraction) {
      return {
        trigger: 'click',
        closeOnSelect: true,
        portal: true,
        preventScrollLock: false
      };
    }
    return {
      trigger: 'hover',
      closeOnSelect: false,
      portal: false,
      preventScrollLock: true
    };
  }, [capabilities.prefersTouchInteraction]);

  return {
    capabilities,
    viewport,
    // Helper functions
    getOptimalTouchTarget,
    getOptimalSpacing,
    shouldUseNativeScrolling,
    getDropdownBehavior,
    // Convenience flags
    isTouchDevice: capabilities.isTouchDevice,
    isMobile: capabilities.isMobile,
    isTablet: capabilities.isTablet,
    isDesktop: capabilities.isDesktop,
    prefersTouchInteraction: capabilities.prefersTouchInteraction
  };
};